import { dayjs } from './date';

/**
 * List Sort
 *
 * The "Sort by" group in a list page's filter menu. Every list sorts on the
 * client, because each loads its whole set; a list states its sorts once, as a
 * catalog, and the web menu and the mobile sheet both draw from it.
 *
 * The chosen sort is remembered per list by `store/list-sort.store`;
 * `resolveSort` turns what it holds back into a key the catalog still has.
 */

export type SortDirection = 'asc' | 'desc';

export type Comparator<T> = (a: T, b: T) => number;

export interface SortSpec<T> {
  /** The option's name in the menu and on its chip. */
  label: string;
  compare: Comparator<T>;
}

export type SortCatalog<T, K extends string> = Record<K, SortSpec<T>>;

/** A copy of the list in the catalog's order for `key`. Ties keep their order. */
export function sortList<T, K extends string>(
  items: readonly T[],
  catalog: SortCatalog<T, K>,
  key: K
): T[] {
  return [...items].sort(catalog[key].compare);
}

/** The catalog as `single` filter options, in the catalog's order. */
export function getSortOptions<K extends string>(
  catalog: SortCatalog<never, K>
): Array<{ value: K; label: string }> {
  return (Object.keys(catalog) as K[]).map((value) => ({
    value,
    label: catalog[value].label,
  }));
}

export function isSortKey<K extends string>(
  catalog: SortCatalog<never, K>,
  value: unknown
): value is K {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(catalog, value)
  );
}

/**
 * The key to sort by: what was stored, while the catalog still has it,
 * otherwise the list's default. A renamed or retired sort never strands a list.
 */
export function resolveSort<K extends string>(
  stored: unknown,
  catalog: SortCatalog<never, K>,
  fallback: K
): K {
  return isSortKey(catalog, stored) ? stored : fallback;
}

/** The filter key every list's "Sort by" group uses. */
export const SORT_FILTER_KEY = 'sort';

export const SORT_FILTER_LABEL = 'Sort by';

/**
 * The "Sort by" group for a filter menu: a single choice, last in the menu.
 * Web's `FilterBar` and mobile's `FilterSheet` both take this shape.
 *
 * A list is always in some order, so the group is never empty: `defaultValue`
 * shows the default ticked (and tagged) while the page passes no value for it,
 * which keeps the default out of the chips, the badge and Clear.
 */
export function getSortFilterDefinition<K extends string>(
  catalog: SortCatalog<never, K>,
  fallback: K
): {
  key: typeof SORT_FILTER_KEY;
  label: string;
  type: 'single';
  options: Array<{ value: K; label: string }>;
  defaultValue: K;
} {
  return {
    key: SORT_FILTER_KEY,
    label: SORT_FILTER_LABEL,
    type: 'single',
    options: getSortOptions(catalog),
    defaultValue: fallback,
  };
}

/**
 * The "Sort by" chip: only while the list is off its default, because the
 * default order is no filter worth showing. Removing it goes back to the default.
 */
export function getSortChip<K extends string>(
  catalog: SortCatalog<never, K>,
  sort: K,
  fallback: K
): { key: typeof SORT_FILTER_KEY; label: string } | null {
  return sort === fallback
    ? null
    : { key: SORT_FILTER_KEY, label: catalog[sort].label };
}

// =============================================================================
// COMPARATORS
// =============================================================================

/**
 * Orders by a value that may be missing. A missing value goes last whichever
 * way the list runs, so "Newest" and "Oldest" both end on the rows without one.
 */
function byValue<T, V>(
  get: (item: T) => V | null | undefined,
  direction: SortDirection,
  diff: (a: V, b: V) => number
): Comparator<T> {
  const sign = direction === 'asc' ? 1 : -1;

  return (a, b) => {
    const left = get(a);
    const right = get(b);
    const leftMissing = left === null || left === undefined;
    const rightMissing = right === null || right === undefined;

    if (leftMissing || rightMissing) {
      return Number(leftMissing) - Number(rightMissing);
    }
    return sign * diff(left, right);
  };
}

export function byNumber<T>(
  get: (item: T) => number | null | undefined,
  direction: SortDirection
): Comparator<T> {
  return byValue(
    (item) => {
      const value = get(item);
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
    },
    direction,
    (a, b) => a - b
  );
}

/** Orders by a date string. An empty or unparsable date counts as missing. */
export function byDate<T>(
  get: (item: T) => string | null | undefined,
  direction: SortDirection
): Comparator<T> {
  return byNumber((item) => {
    const value = get(item);
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.valueOf() : null;
  }, direction);
}

/**
 * Orders by text, ignoring case and accents, with numbers in number order:
 * "Week 2" before "Week 10". A generated plan names its workouts that way and
 * creates them in one second, so their "Newest" tie falls through to here.
 */
export function byText<T>(
  get: (item: T) => string | null | undefined,
  direction: SortDirection = 'asc'
): Comparator<T> {
  return byValue(
    (item) => get(item) || null,
    direction,
    (a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
  );
}

/** The first comparator that tells two rows apart decides. */
export function thenBy<T>(...comparators: Comparator<T>[]): Comparator<T> {
  return (a, b) => {
    for (const compare of comparators) {
      const result = compare(a, b);
      if (result !== 0) return result;
    }
    return 0;
  };
}
